import { useForm, FormProvider } from 'react-hook-form'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import InputField from './shared/InputField'
import styles from './styles.module.scss'
import { SubmitButton } from '@components/ui/Button'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { AddressFormWrapper } from './AddressFormWrapper'
import { CustomerDetailsData, CustomerDetailsSchema } from '../config'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { ErrorField } from './shared/InputField/InputErrorMessage'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import { customerUpdate } from '../api/customerNamesUpdate'
import { customerEmailUpdate } from '../api/customerEmailUpdate'
import { getErrorMessage } from '@api/graphqlShopify'
import { getShopLocale } from '@helpers/env'
import { ISO_3166_COUNTRIES } from '@consts/index'

const normalize = (value: string | null | undefined): string | null =>
  value === '' || value == null ? null : value

function getPhoneCountryList() {
  return Object.entries(ISO_3166_COUNTRIES).map(([_, { alpha2, prefix }]) => {
    return {
      value: alpha2,
      label: prefix,
      availability: true
    }
  })
}

export function ManageCustomerDetails({
  drawerState,
  handleCloseAccountDetailsDrawer
}: {
  drawerState: { data: CustomerDetailsData }
  handleCloseAccountDetailsDrawer: () => void
}) {
  const phoneCountryList = getPhoneCountryList()
  const { refreshUserProfile, makeRequestWithRefreshCheck, token } = useAuth()
  const [buttonState, setButtonState] = useState<SubmitButtonState>('ready')
  const originalCustomer = drawerState.data
  const methods = useForm<CustomerDetailsData & { customError?: string }>({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(CustomerDetailsSchema),
    defaultValues: {
      ...drawerState.data
    }
  })

  const formErrors = methods.formState.errors

  const handleUpdateCustomer = async (data: CustomerDetailsData) => {
    setButtonState('loading')
    try {
      await makeRequestWithRefreshCheck(customerUpdate, data)

      const isUpdatedEmail =
        normalize(data?.email) !== normalize(originalCustomer?.email)
      const isUpdatedPhone =
        normalize(data?.phoneNumber) !==
        normalize(originalCustomer?.phoneNumber)

      if (
        (isUpdatedEmail || isUpdatedPhone) &&
        typeof token === 'string' &&
        !!token
      ) {
        await customerEmailUpdate({
          email: data.email,
          accessToken: token,
          phoneNumber: data?.phoneNumber
        })
      }

      await refreshUserProfile()

      setButtonState('ready')
      handleCloseAccountDetailsDrawer()
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage =
        getErrorMessage(error) || 'Something went wrong. Try again later.'
      methods.setError(
        'customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
      setButtonState('ready')
    } finally {
      setButtonState('ready')
    }
  }

  const defaultPhonePlaceholder =
    phoneCountryList.find(
      country => country.value === getShopLocale().toUpperCase()
    )?.label || '+44'

  return (
    <FormProvider {...methods}>
      <AddressFormWrapper title="Personal">
        <form onSubmit={methods.handleSubmit(handleUpdateCustomer)}>
          <div className={styles.AccountForm__content}>
            <InputField
              name="First Name"
              id="firstName"
              type="text"
              errors={formErrors}
            />
            <InputField
              name="Last Name"
              id="lastName"
              type="text"
              errors={formErrors}
            />
            <InputField
              name="Email"
              id="email"
              type="email"
              errors={formErrors}
            />
            <InputField
              name="Phone"
              id="phoneNumber"
              type="text"
              placeholder={defaultPhonePlaceholder}
              errors={formErrors}
            />
          </div>
          <div className={styles.AccountForm__Buttons}>
            {formErrors.customError?.message && (
              <ErrorField message={formErrors.customError.message} />
            )}
            <ButtonMinimal
              underlined={true}
              onClick={() => handleCloseAccountDetailsDrawer()}
            >
              Cancel
            </ButtonMinimal>
            <SubmitButton type="submit" fullWidth="never" state={buttonState}>
              Save
            </SubmitButton>
          </div>
        </form>
      </AddressFormWrapper>
    </FormProvider>
  )
}
